import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Sticky floating WhatsApp coach button. Mounted on /shop and /shop/:slug
 * for "Talk to a coach" guidance. Avoids appearing on /admin, /login,
 * /register and the checkout flow.
 *
 * Tone: friendly, low-friction, never spammy. Bottom-right on desktop,
 * bottom-center pill on mobile. Single-tap → opens WhatsApp with a warm
 * intro message that can be customised per page (`message` prop).
 *
 * Dismiss state persisted in sessionStorage so a "X" closes it for the
 * rest of the session.
 */
const SS_KEY = "fg_sticky_wa_dismissed_v1";

export default function StickyWhatsAppButton({
  message = "Hi! I'd love some guidance choosing the right wellness products for me.",
  label = "Talk to a coach",
}) {
  const [whatsapp, setWhatsapp] = useState("");
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/config").then(({ data }) => {
      if (!cancelled) setWhatsapp(data?.whatsapp_number || "447424225333");
    }).catch(() => {
      if (!cancelled) setWhatsapp("447424225333");
    });
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !whatsapp) return null;
  const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
  const dismiss = () => {
    try { sessionStorage.setItem(SS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 animate-in slide-in-from-bottom-2 duration-300"
      data-testid="sticky-whatsapp"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-emerald-100 text-emerald-700/60 hover:text-emerald-900 hover:bg-emerald-50 transition shadow-sm"
        data-testid="sticky-whatsapp-dismiss"
      >
        <X className="w-3.5 h-3.5" strokeWidth={1.8} />
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex items-center gap-2 rounded-full px-4 py-3 sm:px-5 sm:py-3.5 bg-emerald-700 text-white shadow-xl shadow-emerald-700/25 hover:bg-emerald-800 transition hover:shadow-emerald-800/30 hover:-translate-y-0.5"
        data-testid="sticky-whatsapp-link"
        aria-label={label}
      >
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15">
          <MessageCircle className="w-4 h-4" strokeWidth={2} />
        </span>
        <span className="text-sm font-semibold tracking-tight">{label}</span>
      </a>
    </div>
  );
}
